import React, { useCallback } from 'react';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useFormattedMessageAndIcon } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-formatted-message-and-icon/index.tsx';
import { toMenuIdFromMenuIdList } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/menu-id/index.tsx';
import { ENTITY_ID } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import PagesIcon from '@atlaskit/icon/core/pages';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import defaultFilters from './defaultFilters.tsx';

const incidentsMenuMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.incidents',

	defaultMessage: 'Incidents',
	description:
		'Menu item label displayed in the operations sidebar navigation. When clicked, takes user to the incidents management section.',
});

const incidentsDefaultFiltersMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.default-filters',

	defaultMessage: 'Default filters',
	description:
		'Expandable menu item label displayed in the operations sidebar. When clicked, shows pre-configured filter options for alerts.',
});

export const IncidentsContent = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { icon: pagesIcon } = useFormattedMessageAndIcon(incidentsMenuMessage, PagesIcon);

	const defaultFiltersMenuId = toMenuIdFromMenuIdList(
		defaultFilters.map(({ incidentSearchQuery }) => ENTITY_ID.INCIDENT(incidentSearchQuery)),
	);

	const onExpandDefaultFilters = useCallback(
		(isExpanded: boolean) => {
			if (isExpanded) {
				fireUIAnalytics(createAnalyticsEvent({}), {
					action: 'clicked',
					actionSubject: 'navigationItem',
					actionSubjectId: 'opsIncidentsDefaultFilters',
					attributes: { itemId: 'incidentsDefaultFilters' },
				});
			}
		},
		[createAnalyticsEvent],
	);

	const onClickFilter = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'navigationItem',
			actionSubjectId: 'opsIncidentsFilter',
			attributes: { itemId: 'incidentsFilter' },
		});
	}, [createAnalyticsEvent]);

	return (
		<ExpandableMenuItemContent>
			<Nav4ExpandableMenuItem
				menuId={defaultFiltersMenuId}
				onExpansionToggle={onExpandDefaultFilters}
			>
				<ExpandableMenuItemTrigger
					interactionName="nav4-sidebar-operations-incidents-default-filters"
					testId={getTestId(defaultFiltersMenuId)}
				>
					{formatMessage(incidentsDefaultFiltersMessage)}
				</ExpandableMenuItemTrigger>
				<ExpandableMenuItemContent>
					{defaultFilters.map((filter) => (
						<Nav4MenuLinkItem
							key={filter.id}
							menuId={ENTITY_ID.INCIDENT(filter.incidentSearchQuery)}
							href={`/jira/ops/incidents?jql=${encodeURIComponent(filter.incidentSearchQuery)}`}
							elemBefore={pagesIcon}
							onClick={onClickFilter}
						>
							{formatMessage(filter.label)}
						</Nav4MenuLinkItem>
					))}
				</ExpandableMenuItemContent>
			</Nav4ExpandableMenuItem>
		</ExpandableMenuItemContent>
	);
};
